/**
 * Upload domain — S3 presigned-URL issuance (GET /home/signed-url).
 *
 * The request contract is stricter than the Joi validator it replaced:
 * `type` and `extension` were nominally optional on the wire but the
 * s3Service hard-requires both (extension since the 2026-05-16 PNG→JPG
 * incident — a `= 'jpg'` default silently corrupted PNG/PDF uploads).
 * Every live caller in web and mobile already sends both, so the schema
 * states the real contract and malformed requests fail at validation
 * with a field-named message instead of deep in the service.
 *
 * `userId` is deliberately ABSENT from the request schema: the backend
 * derives the S3 directory from the authenticated user and must never
 * trust a wire-supplied owner id.
 */
import { z } from 'zod';
import { envelope } from './_primitives';

export const UPLOAD_ROUTES = {
  /** GET ?fileCount=&type=&extension= — presigned PUT URLs (201). */
  signedUrl: 'home/signed-url',
} as const;

/** The slots one signed-url request may ask for, in every folder but receipts. */
export const MAX_SIGNED_URLS_PER_REQUEST = 10;

/**
 * The most files one drop of Add receipts holds. A vendor bringing a year of
 * orders over brings about 120 (Tyler, 2026-10-13); 250 takes the largest
 * set seen so far, 216, in one drop. A drop asks for all its upload slots in
 * one request (SIGNED_URLS_PER_REQUEST), so this is that request's cap too
 * (docs/plans/receipts-redesign.md, Decision 65). Here, not in receipts.ts,
 * because the apps that upload import this file and mobile imports nothing
 * of receipts.
 */
export const MAX_RECEIPTS_PER_UPLOAD = 250;

/**
 * The folders an upload link writes to (#607 row 29, Tyler 2026-09-27):
 * every folder web, admin and mobile ask for, released mobile builds
 * included. Any other `type` used to become a new top-level folder in the
 * bucket. Mobile's 'profile', 'vendor_profile' and 'conversation' are
 * folders of their own beside web's 'user_profile', 'store_profile' and
 * 'chat'; every promised build (ios-86, android-3 to 6) sends exactly
 * mobile's six, so all stay until the floor passes them. 'receipts' is a
 * vendor's tax evidence (#495), a folder of its own so it can be gated
 * apart from the rest, as 'insurance' was split from 'documents'.
 */
export const UPLOAD_TYPES = [
  'listings',
  'inventory',
  'user_profile',
  'store_profile',
  'documents',
  'chat',
  'insurance',
  'receipts',
  'profile',
  'vendor_profile',
  'conversation',
] as const;
export type UploadType = (typeof UPLOAD_TYPES)[number];

/** A megabyte, as the limits below and every message about them count it. */
export const BYTES_PER_MB = 1024 * 1024;
/** The largest photo an upload takes: the backend's image limit
 * (fileValidation.js) before any upload was capped. */
export const MAX_IMAGE_SIZE = 10 * BYTES_PER_MB;
/**
 * The extensions a photo goes by: the image types every app shows
 * (fileValidation.js, ALLOWED_IMAGE_TYPES). A browser shows no HEIC, the
 * iPhone's own, so a photo of another type would be stored and never seen.
 */
export const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp'] as const;
/** The largest document an upload takes, and the receipt reader's limit. */
export const MAX_DOCUMENT_SIZE = 50 * BYTES_PER_MB;

/**
 * Each folder's cap in bytes (#1224, Tyler 2026-09-28: the backend's own
 * limits, so there is one set of them). The backend writes it into the
 * upload's POST policy and S3 refuses a larger file. Message attachments
 * take the document cap, since one can be a PDF as well as a photo. Typed
 * by folder, so a folder added to UPLOAD_TYPES without a cap fails to
 * compile.
 */
export const UPLOAD_SIZE_LIMITS: Readonly<Record<UploadType, number>> = {
  listings: MAX_IMAGE_SIZE,
  inventory: MAX_IMAGE_SIZE,
  user_profile: MAX_IMAGE_SIZE,
  store_profile: MAX_IMAGE_SIZE,
  profile: MAX_IMAGE_SIZE,
  vendor_profile: MAX_IMAGE_SIZE,
  documents: MAX_DOCUMENT_SIZE,
  insurance: MAX_DOCUMENT_SIZE,
  receipts: MAX_DOCUMENT_SIZE,
  chat: MAX_DOCUMENT_SIZE,
  conversation: MAX_DOCUMENT_SIZE,
};

/**
 * How many slots one signed-url request may ask for, by folder. A slot is a
 * signature our server makes locally, with no call to AWS, so the cap bounds
 * a request's size, not work at S3. A drop of receipts asks for all its slots
 * at once and the uploader still sends ten files at a time; every other
 * folder uploads one to ten files (docs/plans/receipts-redesign.md, Decision
 * 65). Typed by folder, so a folder added to UPLOAD_TYPES without a cap fails
 * to compile.
 */
export const SIGNED_URLS_PER_REQUEST: Readonly<Record<UploadType, number>> = {
  listings: MAX_SIGNED_URLS_PER_REQUEST,
  inventory: MAX_SIGNED_URLS_PER_REQUEST,
  user_profile: MAX_SIGNED_URLS_PER_REQUEST,
  store_profile: MAX_SIGNED_URLS_PER_REQUEST,
  profile: MAX_SIGNED_URLS_PER_REQUEST,
  vendor_profile: MAX_SIGNED_URLS_PER_REQUEST,
  documents: MAX_SIGNED_URLS_PER_REQUEST,
  insurance: MAX_SIGNED_URLS_PER_REQUEST,
  receipts: MAX_RECEIPTS_PER_UPLOAD,
  chat: MAX_SIGNED_URLS_PER_REQUEST,
  conversation: MAX_SIGNED_URLS_PER_REQUEST,
};

const SECONDS_PER_MINUTE = 60;

/**
 * How long an upload slot lives, in seconds, by folder: the backend signs
 * each slot for it, and the uploader stops starting files on a set of slots
 * a minute before it ends. A slot is a pass to write one key that works for
 * whoever holds it, so it lives no longer than its upload needs, and never
 * past the 15 minutes AWS's presigned-request guardrail allows (s3:signatureAge;
 * AWS Prescriptive Guidance, "Establishing guardrails and monitoring for
 * presigned URLs", Additional guardrails). Receipts take the 15 minutes: a
 * drop's slots are made in one request and sent ten at a time. Every other
 * folder sends at most ten files on slots made for them, so 3 minutes holds.
 */
export const UPLOAD_SLOT_LIFETIME_SECONDS: Readonly<Record<UploadType, number>> = {
  listings: 3 * SECONDS_PER_MINUTE,
  inventory: 3 * SECONDS_PER_MINUTE,
  user_profile: 3 * SECONDS_PER_MINUTE,
  store_profile: 3 * SECONDS_PER_MINUTE,
  profile: 3 * SECONDS_PER_MINUTE,
  vendor_profile: 3 * SECONDS_PER_MINUTE,
  documents: 3 * SECONDS_PER_MINUTE,
  insurance: 3 * SECONDS_PER_MINUTE,
  receipts: 15 * SECONDS_PER_MINUTE,
  chat: 3 * SECONDS_PER_MINUTE,
  conversation: 3 * SECONDS_PER_MINUTE,
};

/**
 * A signed-url request asks for no more slots than its folder takes in one
 * request (SIGNED_URLS_PER_REQUEST). The folder decides, so the rule sits on
 * the request, not on fileCount alone; the vendor API's upload request runs
 * it too. Refused as fileCount too big, the same issue a plain cap gives.
 */
export const slotsWithinFolderCap = (query: { fileCount: number; type: UploadType }, ctx: z.RefinementCtx) => {
  const cap = SIGNED_URLS_PER_REQUEST[query.type];
  if (query.fileCount > cap) {
    ctx.addIssue({
      code: 'too_big',
      origin: 'number',
      maximum: cap,
      inclusive: true,
      input: query.fileCount,
      path: ['fileCount'],
      message: `fileCount must be at most ${cap} for ${query.type}`,
    });
  }
};

export const SignedUrlsQuerySchema = z.object({
  // Query params arrive as strings — coerce, then bound. The most a request
  // takes is its folder's, so the upper bound is slotsWithinFolderCap's alone:
  // a bound here too would refuse one request twice.
  fileCount: z.coerce
    .number({ error: 'fileCount is required' })
    .int('fileCount must be an integer')
    .min(1, 'fileCount must be at least 1'),
  type: z.enum(UPLOAD_TYPES, { error: 'type must be one of the upload folders' }),
  extension: z
    .string({
      error:
        'extension is required (e.g. "png", "pdf"). Derive from File.name on the client.',
    })
    .min(
      1,
      'extension is required (e.g. "png", "pdf"). Derive from File.name on the client.'
    ),
}).superRefine(slotsWithinFolderCap);
export type SignedUrlsQuery = z.infer<typeof SignedUrlsQuerySchema>;

/** A presigned S3 POST (#1224): the form's target and the fields S3
 * checks against the policy the backend signed, which holds the upload's
 * folder to a size cap (a PUT cannot bound the size of its body). The
 * file goes into the form after every one of these fields, as `file`. */
export const PresignedPostSchema = z.object({
  url: z.url(),
  fields: z.record(z.string(), z.string()),
});
export type PresignedPost = z.infer<typeof PresignedPostSchema>;

/** One presigned upload slot. `post` is the upload every app sends
 * (@gearghost/uploader), a POST whose policy holds the file to its
 * folder's cap (#1224). `url` is the time-limited PUT target released
 * mobile builds still upload with, unbounded in size; it goes when the
 * floor passes them (#1107). `s3Path` is the durable key the client
 * writes back to our API after uploading; `contentType` must be echoed as
 * the PUT's Content-Type header or S3 rejects the signature (the POST
 * carries it among its fields). */
export const SignedUrlEntrySchema = z.object({
  url: z.url(),
  s3Path: z.string().min(1),
  contentType: z.string().min(1),
  post: PresignedPostSchema,
});

export const SignedUrlsSchema = z.array(SignedUrlEntrySchema);
export const SignedUrlsEnvelopeSchema = envelope(SignedUrlsSchema);
export const unwrapSignedUrls = (
  response: z.infer<typeof SignedUrlsEnvelopeSchema>
) => response.data;
